/**
 * @fileoverview Centralized Date/Time Utilities
 * @description Pure functions and constants for date formatting, parsing,
 * and arithmetic. No Angular dependency — usable in any context.
 */

// ── Format constants ──────────────────────────────────────────
export const DATE_FORMAT = 'dd/MM/yyyy';
export const DATETIME_FORMAT = 'dd/MM/yyyy HH:mm';
export const TIME_FORMAT = 'HH:mm:ss';
export const EXPORT_DATETIME_FORMAT = 'yyyy-MM-dd HH:mm';
export const EXPORT_DATE_FORMAT = 'yyyy-MM-dd';
export const API_DATE_FORMAT = 'yyyy-MM-dd';

export type DateFormatPreset = 'date' | 'datetime' | 'datetimetz' | 'time' | 'short';

export const DATE_FORMAT_PRESETS: Record<DateFormatPreset, string> = {
  date: DATE_FORMAT,
  datetime: DATETIME_FORMAT,
  datetimetz: 'dd/MM/yyyy HH:mm (z)',
  time: TIME_FORMAT,
  short: 'dd/MM/yy',
};

// ── Parsing ───────────────────────────────────────────────────

/**
 * Safely parses a value into a Date object.
 * Returns null when the value cannot be converted to a valid date.
 */
export function parseDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    if (DATE_ONLY_REGEX.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d);
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/** Returns true when `value` can be converted to a valid Date. */
export function isValidDate(value: unknown): boolean {
  return parseDate(value) !== null;
}

// ── Formatting ────────────────────────────────────────────────

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Formats a Date for CSV / file export.
 * Returns `yyyy-MM-dd HH:mm` when the time portion is non-zero,
 * otherwise `yyyy-MM-dd`.
 */
export function formatDateForExport(date: Date): string {
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  if (hours !== '00' || minutes !== '00') {
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }
  return `${year}-${month}-${day}`;
}

/**
 * Returns `yyyy-MM-dd` for API serialization.
 * Falls back to empty string when the input is not a valid date.
 */
export function toISODateString(value: unknown): string {
  const date = parseDate(value);
  if (!date) return '';
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  return `${year}-${month}-${day}`;
}

// ── Arithmetic ────────────────────────────────────────────────

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const MS_PER_HOUR = 1000 * 60 * 60;

/**
 * Checks whether two dates fall within the same calendar month and year.
 * When `date2` is omitted the current date is used.
 */
export function isSameMonth(date1: unknown, date2?: unknown): boolean {
  const d1 = parseDate(date1);
  const d2 = date2 !== undefined ? parseDate(date2) : new Date();
  if (!d1 || !d2) return false;
  return d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
}

/** Absolute number of full days between two dates. */
export function daysBetween(start: unknown, end: unknown): number {
  const d1 = parseDate(start);
  const d2 = parseDate(end);
  if (!d1 || !d2) return 0;
  return Math.abs(Math.floor((d2.getTime() - d1.getTime()) / MS_PER_DAY));
}

/** Absolute number of full hours between two dates. */
export function hoursBetween(start: unknown, end: unknown): number {
  const d1 = parseDate(start);
  const d2 = parseDate(end);
  if (!d1 || !d2) return 0;
  return Math.abs(Math.floor((d2.getTime() - d1.getTime()) / MS_PER_HOUR));
}

// ── Timezone helpers ─────────────────────────────────────────

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Returns true when the string is a date-only value (`yyyy-MM-dd`)
 * with no time or timezone component.
 */
export function isDateOnly(value: string): boolean {
  return DATE_ONLY_REGEX.test(value);
}

/**
 * Formats a Date (or parseable value) as a UTC ISO 8601 datetime string.
 *
 * Example output: `"2024-12-25T13:30:00.000Z"`
 *
 * Returns empty string when the input is not a valid date.
 * Use for datetime fields sent to the backend (`declaredAt`, etc.).
 * The backend always expects and returns UTC timestamps.
 */
export function toISODateTimeString(value: unknown): string {
  const date = parseDate(value);
  if (!date) return '';
  return date.toISOString();
}
